import type { PaymentAllocationLine, PaymentResponse } from '../../api/types';
import { parsePaise } from './allocation';

export function canAllocateReceiptCredit(payment: PaymentResponse, readOnly = false) {
  return (
    !readOnly &&
    payment.direction === 'IN' &&
    payment.status === 'PAID' &&
    parsePaise(payment.unallocatedCredit) > 0n
  );
}

// The append endpoint has no idempotency key. Compare fresh detail with the exact
// baseline and submitted delta; unrelated/concurrent changes never permit replay.
export function reconcileReceiptCredit(
  before: PaymentResponse,
  after: PaymentResponse,
  lines: PaymentAllocationLine[],
) {
  if (
    before.id !== after.id ||
    before.counterpartyUserId !== after.counterpartyUserId ||
    before.amount !== after.amount ||
    after.direction !== 'IN' ||
    after.status !== 'PAID'
  )
    return 'changed';
  const signature = (a: PaymentResponse['allocations'][number]) =>
    JSON.stringify([a.id, a.shipmentId, a.purpose, a.amount, a.tdsAmount, a.status, a.voidedAt]);
  if (
    !before.allocations.every((a) => after.allocations.some((b) => signature(a) === signature(b)))
  )
    return 'changed';
  const additions = after.allocations.filter((a) => !before.allocations.some((b) => b.id === a.id));
  if (
    !additions.length &&
    before.allocations.length === after.allocations.length &&
    parsePaise(before.unallocatedCredit) === parsePaise(after.unallocatedCredit)
  )
    return 'unchanged';
  const key = (a: PaymentAllocationLine) =>
    `${a.shipmentId}:${a.purpose}:${parsePaise(a.amount)}:${parsePaise(a.tdsAmount)}`;
  const expected = lines.map(key).sort();
  const actual = additions
    .filter((a) => a.status === 'ACTIVE')
    .map(key)
    .sort();
  const cash = lines.reduce((sum, a) => sum + parsePaise(a.amount), 0n);
  return additions.length === lines.length &&
    JSON.stringify(expected) === JSON.stringify(actual) &&
    parsePaise(after.unallocatedCredit) === parsePaise(before.unallocatedCredit) - cash
    ? 'confirmed'
    : 'changed';
}
